import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { doc, getCountFromServer, onSnapshot } from "firebase/firestore";
import { auth } from "../../firebase";
import Window from "../../components/Window";
import Icon, { IconTile } from "../../components/Icon";
import { PostComposer, Postcard } from "../../components/Postcards";
import { getUsername } from "../../penpal";
import { Thread } from "../../rooms";
import {
  CIRCLE_LIMITS,
  Circle,
  JoinRequest,
  Member,
  approveRequest,
  askToJoin,
  cancelRequest,
  circlePostsCol,
  circleRepliesCol,
  declineRequest,
  editCircle,
  isLead,
  leaveCircle,
  membersCol,
  passRole,
  rememberCircle,
  removeMember,
  requestsCol,
  setMates,
  startCirclePost,
  useCircle,
  useMembership,
} from "../../circles";
import { useBlocks } from "../../blocks";
import "../../styles.css";

export default function CirclePage() {
  const { circleId = "" } = useParams();
  const [user] = useAuthState(auth);
  const c = useCircle(circleId);
  const member = useMembership(circleId, user?.uid);

  if (c === undefined || (user && member === undefined)) return <p className="empty">Opening the circle...</p>;
  if (c === null) {
    return (
      <div className="window">
        <p className="empty">
          This circle isn't here (or isn't open yet). <Link to="/circles">Back to Private Circles</Link>
        </p>
      </div>
    );
  }

  return (
    <>
      <CircleHeader c={c} />
      {member ? <MemberView c={c} /> : <OutsiderView c={c} />}
    </>
  );
}

function CircleHeader({ c }: { c: Circle }) {
  return (
    <Window title="private_circle.htm" tone="lavender">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="window-heading flex items-center gap-3">
            <IconTile name="lock" tone="violet" size={44} /> {c.name}
          </h1>
          <p className="window-sub !mb-1">{c.purpose}</p>
          <p className="text-sm text-ink-soft">
            Quartermaster: <strong>@{c.quartermasterName}</strong>
            {c.status === "pending" && " · waiting for a moderator to approve it"}
            {c.status === "paused" && " · paused until a new quartermaster is chosen"}
          </p>
        </div>
        <Link to="/circles" className="btn btn-plain btn-sm">
          <Icon name="back" size={16} /> All circles
        </Link>
      </div>
      {c.guidelines && (
        <details className="mt-3">
          <summary className="cursor-pointer text-sm font-bold">This circle's guidelines</summary>
          <p className="mt-2 whitespace-pre-wrap text-sm">{c.guidelines}</p>
        </details>
      )}
    </Window>
  );
}

// ---------- not a member yet ----------

function OutsiderView({ c }: { c: Circle }) {
  const [user] = useAuthState(auth);
  const [pending, setPending] = useState<boolean | undefined>(undefined);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;
    return onSnapshot(
      doc(requestsCol(c.id), user.uid),
      (s) => setPending(s.exists()),
      () => setPending(false)
    );
  }, [user, c.id]);

  if (!user) return null;
  if (c.status !== "approved") {
    return (
      <div className="window">
        <p className="empty">This circle isn't taking new members right now.</p>
      </div>
    );
  }

  const ask = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const username = await getUsername(user.uid);
      if (username === "unknown") throw new Error("no-username");
      await askToJoin(user.uid, username, c.id, note);
    } catch (err: any) {
      console.error(err);
      setError(
        err?.message === "no-username"
          ? "Choose a username on My Profile first."
          : user.emailVerified
          ? "Sorry, that didn't send. Please try again."
          : "Please verify your email first. Use the reminder at the top of the page to resend the link."
      );
    }
    setBusy(false);
  };

  return (
    <Window title="ask_to_join.txt" tone="tan">
      {pending ? (
        <div className="flex flex-col items-start gap-3">
          <p>
            <strong>Your request is waiting.</strong> The quartermaster or a first
            mate will let you in soon. ✿
          </p>
          <button type="button" className="btn btn-plain btn-sm" onClick={() => cancelRequest(user.uid, c.id)}>
            Cancel my request
          </button>
        </div>
      ) : (
        <form onSubmit={ask} className="flex flex-col gap-3">
          <p className="text-sm">
            Posts in this circle are only seen by its members. Ask to join, and the
            quartermaster will welcome you in.
          </p>
          <label className="field">
            <span className="field-label">A note to the quartermaster (optional)</span>
            <textarea
              className="input"
              value={note}
              maxLength={CIRCLE_LIMITS.note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="A little about you and why this circle feels right"
            />
          </label>
          {error && (
            <p className="error-text" role="alert">
              {error}
            </p>
          )}
          <div>
            <button type="submit" className="btn" disabled={busy}>
              <Icon name="request" size={16} /> Ask to join
            </button>
          </div>
        </form>
      )}
    </Window>
  );
}

// ---------- members ----------

function MemberView({ c }: { c: Circle }) {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const { ids: blocked } = useBlocks();
  const [members, setMembers] = useState<Member[]>([]);
  const [posts, setPosts] = useState<Thread[] | null>(null);
  const [comments, setComments] = useState<Record<string, number>>({});
  const lead = isLead(c, user?.uid);
  const qm = c.quartermaster === user?.uid;

  // Remember this circle as one of yours (for Delete my account / Download my data).
  useEffect(() => {
    if (user) rememberCircle(user.uid, c.id).catch(() => undefined);
  }, [user, c.id]);

  useEffect(
    () =>
      onSnapshot(
        membersCol(c.id),
        (snap) =>
          setMembers(
            snap.docs
              .map((d) => ({ id: d.id, ...(d.data() as Omit<Member, "id">) }))
              .sort((a, b) => a.username.localeCompare(b.username))
          ),
        () => setMembers([])
      ),
    [c.id]
  );

  useEffect(
    () =>
      onSnapshot(
        circlePostsCol(c.id),
        (snap) => {
          const list = snap.docs
            .map((d) => ({ id: d.id, ...(d.data() as Omit<Thread, "id">) }))
            .filter((p) => !p.removed)
            .sort((a, b) => (b.lastReplyAt?.toMillis() ?? 0) - (a.lastReplyAt?.toMillis() ?? 0));
          setPosts(list);
          list.forEach((p) =>
            getCountFromServer(circleRepliesCol(c.id, p.id))
              .then((n) => setComments((prev) => ({ ...prev, [p.id]: n.data().count })))
              .catch(() => undefined)
          );
        },
        () => setPosts([])
      ),
    [c.id]
  );

  if (!user) return null;
  const visible = (posts || []).filter(
    (p) => !(p.uid && blocked.has(p.uid)) && (!p.hidden || lead)
  );

  const leave = async () => {
    const msg = qm
      ? c.mates?.length
        ? "Leave this circle? Your first mate will become quartermaster."
        : "Leave this circle? With no first mate to take over, the circle will pause until a moderator picks a new quartermaster. (You can pass the role to someone first, below.)"
      : "Leave this circle? You can ask to join again later.";
    if (!window.confirm(msg)) return;
    try {
      await leaveCircle(c, user.uid);
      navigate("/circles");
    } catch (err) {
      console.error(err);
      alert("Sorry, that didn't work. Please try again.");
    }
  };

  return (
    <>
      {c.status === "pending" && (
        <p className="notice text-sm">
          <strong>Waiting for approval.</strong> A moderator will look over your
          circle soon. Once it's approved, people can ask to join and you can start
          posting.
        </p>
      )}

      {lead && c.status === "approved" && <JoinRequests c={c} />}

      {c.status === "approved" && (
        <Window title="write_a_post.exe" tone="blue">
          <details>
            <summary className="btn btn-blue cursor-pointer list-none">
              <Icon name="edit" size={16} /> Post in {c.name}
            </summary>
            <PostComposer
              titleLabel="Post title"
              titlePlaceholder="What's on your mind?"
              bodyPlaceholder="Only members of this circle will see this."
              categoryOptions={[]}
              anonymousHint="Other members see 'anonymous' instead of your username. Site moderators can still see who wrote it if it's reported. Anonymous posts can't receive kudos."
              submitLabel="Post it"
              onPost={async (p) => {
                const username = await getUsername(user.uid);
                const id = await startCirclePost(user.uid, username, c.id, p);
                navigate(`/circles/${c.id}/post/${id}`);
              }}
            />
          </details>
        </Window>
      )}

      {posts === null ? (
        <p className="empty">Gathering the circle...</p>
      ) : visible.length === 0 ? (
        <div className="window">
          <p className="empty">No posts yet. Say hello to your circle! ✿</p>
        </div>
      ) : (
        <div className="grid gap-5 pt-2 sm:grid-cols-2 xl:grid-cols-3">
          {visible.map((p) => (
            <Postcard
              key={p.id}
              to={`/circles/${c.id}/post/${p.id}`}
              subject={p.title}
              message={p.hidden ? "" : p.body}
              cw={p.hidden ? "hidden by a lead" : p.cw}
              color={p.color}
              from={p.anonymous ? "— anonymous ✿" : `— @${p.username}`}
              footnote={comments[p.id] ? `${comments[p.id]} comment${comments[p.id] === 1 ? "" : "s"}` : undefined}
            />
          ))}
        </div>
      )}

      <MembersPanel c={c} members={members} />

      {qm && <EditCircle c={c} />}

      <div className="text-center">
        <button type="button" className="btn btn-plain btn-sm" onClick={leave}>
          <Icon name="logout" size={14} /> Leave this circle
        </button>
      </div>
    </>
  );
}

function JoinRequests({ c }: { c: Circle }) {
  const [requests, setRequests] = useState<JoinRequest[]>([]);
  useEffect(
    () =>
      onSnapshot(
        requestsCol(c.id),
        (snap) => setRequests(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<JoinRequest, "id">) }))),
        () => setRequests([])
      ),
    [c.id]
  );
  if (requests.length === 0) return null;
  return (
    <Window title={`join_requests (${requests.length})`} tone="pink">
      <ul className="flex flex-col gap-3">
        {requests.map((r) => (
          <li key={r.id} className="profile-box bg-white">
            <div className="profile-box-body flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <Link to={`/profile/${r.id}`} className="font-bold">
                  @{r.username}
                </Link>
                {r.note && <p className="text-sm whitespace-pre-wrap">"{r.note}"</p>}
              </div>
              <button type="button" className="btn btn-sm" onClick={() => approveRequest(c.id, r)}>
                <Icon name="check" size={14} /> Let them in
              </button>
              <button type="button" className="btn btn-plain btn-sm" onClick={() => declineRequest(c.id, r.id)}>
                Decline
              </button>
            </div>
          </li>
        ))}
      </ul>
    </Window>
  );
}

function MembersPanel({ c, members }: { c: Circle; members: Member[] }) {
  const [user] = useAuthState(auth);
  const lead = isLead(c, user?.uid);
  const qm = c.quartermaster === user?.uid;
  const mates = c.mates || [];

  const act = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
    } catch (err) {
      console.error(err);
      alert("Sorry, that didn't work. Please try again.");
    }
  };

  return (
    <Window title={`members (${members.length})`} tone="mint">
      <ul className="flex flex-col gap-2">
        {members.map((m) => {
          const isQm = m.id === c.quartermaster;
          const isMate = mates.includes(m.id);
          return (
            <li key={m.id} className="flex flex-wrap items-center gap-2">
              <Link to={`/profile/${m.id}`} className="font-bold">
                @{m.username}
              </Link>
              {isQm && <span className="chip on">quartermaster</span>}
              {isMate && <span className="chip">first mate</span>}
              {m.id === user?.uid && <span className="text-xs text-ink-soft">(you)</span>}
              <span className="flex-1" />
              {qm && !isQm && (
                <>
                  {isMate ? (
                    <button
                      type="button"
                      className="text-sm underline"
                      onClick={() => act(() => setMates(c.id, mates.filter((x) => x !== m.id)))}
                    >
                      Remove first mate
                    </button>
                  ) : (
                    mates.length < 2 && (
                      <button
                        type="button"
                        className="text-sm underline"
                        onClick={() => act(() => setMates(c.id, [...mates, m.id]))}
                      >
                        Make first mate
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    className="text-sm underline"
                    onClick={() =>
                      window.confirm(
                        `Make @${m.username} the quartermaster? You'll stay in the circle as a member.`
                      ) && act(() => passRole(c, m))
                    }
                  >
                    Pass quartermaster role
                  </button>
                </>
              )}
              {lead && !isQm && m.id !== user?.uid && (!isMate || qm) && (
                <button
                  type="button"
                  className="text-sm underline"
                  onClick={() =>
                    window.confirm(`Remove @${m.username} from the circle? They won't be told why.`) &&
                    act(() => removeMember(c, m.id))
                  }
                >
                  Remove
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {qm && (
        <p className="mt-3 text-xs text-ink-soft">
          First mates (up to 2) can let people in, remove members, and hide posts
          that break the circle's guidelines. If you leave, your first mate becomes
          quartermaster.
        </p>
      )}
    </Window>
  );
}

function EditCircle({ c }: { c: Circle }) {
  const [purpose, setPurpose] = useState(c.purpose);
  const [guidelines, setGuidelines] = useState(c.guidelines);
  const [saved, setSaved] = useState(false);
  return (
    <Window title="quartermaster_tools.ini" tone="tan">
      <details>
        <summary className="cursor-pointer font-bold">Edit the circle's description and guidelines</summary>
        <form
          className="mt-3 flex flex-col gap-3"
          onSubmit={async (e) => {
            e.preventDefault();
            await editCircle(c.id, purpose, guidelines).catch(() => alert("Sorry, that didn't save."));
            setSaved(true);
          }}
        >
          <label className="field">
            <span className="field-label">Who it's for and what it's about</span>
            <textarea className="input" value={purpose} maxLength={CIRCLE_LIMITS.purpose} onChange={(e) => setPurpose(e.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">Circle guidelines</span>
            <textarea
              className="input"
              value={guidelines}
              maxLength={CIRCLE_LIMITS.guidelines}
              onChange={(e) => setGuidelines(e.target.value)}
            />
          </label>
          <div className="flex items-center gap-3">
            <button type="submit" className="btn btn-sm">
              Save
            </button>
            {saved && <span className="success-text text-sm">Saved ✿</span>}
          </div>
        </form>
      </details>
    </Window>
  );
}
